import { useMemo } from 'react';
import type { HeatCell, HeatmapKind } from '@/lib/stats';
import { fc, GeoJsonLayer } from './layers';

/** Bus speed: slow (orange-red) → mid (neutral) → fast (blue). Cells need this many positions. */
export const SPEED_STOPS: Array<[number, string]> = [
  [5, '#c2410c'],
  [15, '#f59e0b'],
  [22, '#d6d3d1'],
  [32, '#2563eb'],
];
const MIN_BUS_POSITIONS = 20;
/** Passengers: one hue, light → dark. */
export const DEMAND_RAMP = ['rgba(99,102,241,0)', '#c7d2fe', '#818cf8', '#4f46e5', '#312e81'];

/**
 * Heatmaps from the summary tables. Both layers stay mounted (empty when off) so they keep their
 * place under the stations and buses.
 */
export function HeatmapLayers({ kind, cells }: { kind: HeatmapKind | null; cells: HeatCell[] }) {
  const data = useMemo(() => {
    const max = Math.max(1, ...cells.map((c) => c[2]));
    const points = (k: HeatmapKind) =>
      kind !== k
        ? fc([])
        : fc(
            cells
              .filter((c) => k === 'user' || c[2] >= MIN_BUS_POSITIONS)
              .map((c) => ({
                type: 'Feature' as const,
                geometry: { type: 'Point' as const, coordinates: [c[0], c[1]] },
                // Weight on a log scale: a few very busy cells should not hide the rest.
                properties: { n: c[2], v: c[3], w: Math.log1p(c[2]) / Math.log1p(max) },
              })),
          );
    return { bus: points('bus'), user: points('user') };
  }, [kind, cells]);

  return (
    <>
      <GeoJsonLayer
        data={data.user}
        layers={[
          {
            type: 'heatmap',
            paint: {
              'heatmap-weight': ['get', 'w'],
              'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 16, 2],
              'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 22, 17, 45],
              'heatmap-opacity': 0.8,
              'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                ...DEMAND_RAMP.flatMap((c, i) => [i / (DEMAND_RAMP.length - 1), c]),
              ],
            } as never,
          },
        ]}
      />
      <GeoJsonLayer
        data={data.bus}
        layers={[
          {
            type: 'circle',
            paint: {
              'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 12, 3.5, 15, 14, 17, 50],
              'circle-color': ['interpolate', ['linear'], ['get', 'v'], ...SPEED_STOPS.flat()],
              'circle-opacity': ['interpolate', ['linear'], ['get', 'w'], 0, 0.35, 1, 0.85],
              'circle-blur': 0.4,
            } as never,
          },
        ]}
      />
    </>
  );
}
